import { Box } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import FilterSidebar from 'components/filter-sidebar';
import List, { HeadCell } from 'components/list/list';
import Paper from 'components/paper';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import { useGet } from 'hooks/use-get';
import { usePagination } from 'hooks/use-pagination';
import { Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import _ from 'underscore';
import { getSiteSettingsOverview } from 'views/site-settings/api/site-settings';
import SiteRow from './components/site-row';
import SiteSettingsTabsHeader from './components/site-settings-tabs-header';
import { SiteTab } from 'views/site-settings/types/site-tab.type';
import SiteSettingsTabs from './components/sites-sub-tabs';
import { Site } from 'views/site-settings/types/site.type';
import { useHistory, useParams } from 'react-router';
import { useSiteSettingsColumns } from 'views/site-settings/hooks/use-site-settings-columns';
import LoadingSpinner from 'components/loading-spinner';

type ListSitesProps = {};

const PAGE_NAME_ID = 'siteSettings.listSites';

interface ListSiteParams {
  siteTab: SiteTab;
}

interface ListProps {
  sites: Site[];
  isLoading: boolean;
  options: HeadCell[];
  setSites: React.Dispatch<React.SetStateAction<Site[]>>;
}

const SitesList: React.FC<ListProps> = ({ sites, isLoading, options, setSites }) => {
  return (
    <List
      isLoading={isLoading}
      rows={sites}
      sort={setSites}
      options={options}
      sx={{
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto',
      }}
      noResult={{
        show: !sites?.length,
        text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
      }}>
      {sites.map((site, idx) => {
        return <SiteRow site={site} key={site.globalSiteId ?? idx} />;
      })}
    </List>
  );
};

const ListSites: React.FC<ListSitesProps> = () => {
  const intl = useIntl();
  const { siteTab } = useParams<ListSiteParams>();
  const { account } = useAccountState();
  const PAGINATION_LIMIT = 10;
  const history = useHistory();
  const columns = useSiteSettingsColumns();

  // #region STATE
  const [siteSearchValue, setSiteSearchValue] = useState<string>('');
  const [rows, setRows] = useState<Site[]>([]);
  // #endregion STATE

  // #region FETCH DATA

  const { data, isLoading } = useGet(
    ['getSiteSettingsOverview', account.company!.companyId],
    ({ queryKey }) => getSiteSettingsOverview(String(queryKey[1])),
    {
      enabled: !!account.company?.companyId,
    }
  );
  // #endregion FETCH DATA

  // #region HELPERS

  const renderListColumns = useMemo(() => {
    if (isLoading || siteTab === SiteTab.INACTIVE) {
      return columns.inactiveSitesColumns;
    }
    else if (siteTab === SiteTab.SITES_WITHOUT_MAPPING) {
      return columns.sitesWithoutMapping;
    }

    return columns.activeSitesColumns;
  }, [siteTab, isLoading]);


  const filteredSites = useMemo(() => {
    const sites =
      siteTab === SiteTab.INACTIVE
        ? data?.inactiveSites ?? []
        : siteTab === SiteTab.SITES_WITHOUT_MAPPING
          ? data?.sitesWithoutMapping ?? []
          : data?.activeSites ?? [];

    const search = siteSearchValue.trim().toLowerCase();

    return sites
      .filter(site => !search || site.name.toLowerCase().includes(search));
  }, [siteTab, siteSearchValue, data]);

  useEffect(() => {
    setRows(_.sortBy(filteredSites, 'name'));
  }, [filteredSites]);


  useEffect(() => {
    if (!siteTab) history.push('/site-settings/sites');

    if (window.location.pathname.endsWith(`/site-settings/${siteTab}/`)) history.push(`/site-settings/${siteTab}`); // remove trailing slash to avoid issues with breadcrumbs

    siteTab == SiteTab.ACTIVE ? data?.activeSites :
      (siteTab == SiteTab.INACTIVE ? data?.inactiveSites : data?.sitesWithoutMapping);
  }, [siteTab, history, data]);




  // #endregion HELPERS

  return (
    <>
      <SiteSettingsTabsHeader utils={<Breadcrumbs />} />

      <FilterSidebar>
        <TextField
          label={intl.formatMessage({ id: 'general.search' })}
          value={siteSearchValue}
          endAdornment={<Search size={18} />}
          onChange={(event) => {
            setSiteSearchValue(event.target.value);
          }}
        />
      </FilterSidebar>

      <SiteSettingsTabs
        aSites={data?.activeSites?.length ?? 0}
        iSites={data?.inactiveSites?.length ?? 0}
        oSites={data?.sitesWithoutMapping?.length ?? 0}
      />
      <Paper transparent sx={{ display: 'flex', borderTopLeftRadius: { xs: 0, md: '1rem' }, flexDirection: 'column', gap: 4 }}>
        <Box display="flex" flexDirection="column" gap={4} sx={{ borderTopLeftRadius: { xs: 0, md: '1rem' } }}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <SitesList
                key={siteTab}
                options={renderListColumns}
                sites={rows}
                isLoading={isLoading}
                setSites={setRows}
              />
            </>
          )}
        </Box>
      </Paper>

    </>
  );
};

export default ListSites;
